import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Message, Form, Input, List, Header, Icon, Loader, Segment } from 'semantic-ui-react'
import './deleteModal.css';

/**
 * Enhanced Delete Literary Group Modal
 * Provides comprehensive delete confirmation with impact analysis and delete type selection
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Object} props.litGroup - Literary group to delete
 * @param {Function} props.onConfirm - Callback when delete is confirmed
 * @param {Function} props.onCancel - Callback when delete is cancelled
 * @param {Function} props.getDeleteImpact - Function to fetch delete impact analysis
 * @param {boolean} props.loading - Whether delete operation is in progress
 * @param {string} props.userRole - Current user's role for permission checking
 * @returns {JSX.Element} DeleteLitGroupModal component
 */
const DeleteLitGroupModal = ({
  open,
  litGroup,
  onConfirm,
  onCancel,
  getDeleteImpact,
  loading = false,
  userRole = 'user'
}) => {
  const [deleteType, setDeleteType] = useState('soft');
  const [confirmationText, setConfirmationText] = useState('');
  const [impactData, setImpactData] = useState(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const [impactError, setImpactError] = useState(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setDeleteType('soft');
      setConfirmationText('');
      setImpactData(null);
      setImpactError(null);
    }
  }, [open]);

  // Fetch impact analysis when modal opens and litGroup is available
  useEffect(() => {
    if (open && litGroup && litGroup.uuid && getDeleteImpact) {
      setImpactLoading(true);
      setImpactError(null);

      getDeleteImpact(litGroup.uuid)
        .then(data => {
          console.log('Delete impact data:', data);
          setImpactData(data.impactAnalysis || data);
          setImpactLoading(false);
        })
        .catch(error => {
          console.error('Error fetching delete impact:', error);
          setImpactError('Failed to load impact analysis. You can still proceed with deletion.');
          setImpactLoading(false);
        });
    }
  }, [open, litGroup, getDeleteImpact]);

  // Handle delete type change - clear confirmation text when switching types
  const handleDeleteTypeChange = useCallback((e, { value }) => {
    setDeleteType(value);
    setConfirmationText('');
  }, []);

  // Handle confirmation text change
  const handleConfirmationTextChange = useCallback((e, { value }) => {
    setConfirmationText(value);
  }, []);

  // Handle confirm delete
  const handleConfirmDelete = useCallback(() => {
    if (onConfirm) {
      onConfirm(deleteType);
    }
  }, [onConfirm, deleteType]);

  // Check if user can confirm deletion
  // Hard delete requires typing "HARD DELETE", all others require "DELETE"
  const requiredConfirmation = deleteType === 'hard' ? 'HARD DELETE' : 'DELETE';
  const canConfirm = confirmationText.toUpperCase() === requiredConfirmation && !loading && !impactLoading;

  // Get delete type description
  const getDeleteTypeDescription = (type) => {
    switch (type) {
      case 'soft':
        return 'Marks the literary group as deleted but preserves all data for licensing/contract purposes. This is the safest option and can be undone.';
      case 'archive':
        return 'Marks the literary group as archived for reference. All data is preserved but the group is hidden from normal operations.';
      case 'hard':
        return 'Permanently removes all data including database records and S3 files. This action cannot be undone and should only be used when no legal obligations remain.';
      default:
        return '';
    }
  };

  // Get delete action word
  const getDeleteActionWord = (type) => {
    switch (type) {
      case 'soft':
        return 'Delete';
      case 'archive':
        return 'Archive';
      case 'hard':
        return 'Permanently Delete';
      default:
        return 'Delete';
    }
  };

  return (
    <Modal open={open} size="small" closeOnDimmerClick={false}
      style={{ zIndex: 10000 }}  // Add this
      dimmer={{ style: { zIndex: 9999 } }}  // And this for the background
    >
      <Modal.Header>
        <Icon name="warning sign" color="red" />
        {getDeleteActionWord(deleteType)} Literary Group
      </Modal.Header>

      <Modal.Content>
        {/* Literary Group Info */}
        {litGroup && (
          <Message info>
            <Message.Header>You are about to {deleteType} delete:</Message.Header>
            <p><strong>{litGroup.name}</strong> ({typeof litGroup.type === 'object' ? litGroup.type?.name : litGroup.type || 'N/A'})</p>
          </Message>
        )}

        {/* Impact Analysis */}
        <Header as="h4" dividing>
          <Icon name="list" />
          Impact Analysis
        </Header>

        {impactLoading ? (
          <Segment>
            <Loader active inline="centered" />
            <p style={{ textAlign: 'center', marginTop: '10px' }}>
              Analyzing what will be affected...
            </p>
          </Segment>
        ) : impactError ? (
          <Message warning>
            <Message.Header>Impact Analysis Unavailable</Message.Header>
            <p>{impactError}</p>
          </Message>
        ) : impactData ? (
          <Segment>
            <p><strong>This will affect:</strong></p>
            <List bulleted>
              <List.Item>
                <strong>Literary Group:</strong> "{impactData.litGroupName || litGroup?.name}"
              </List.Item>
              {impactData.totals && (
                <>
                  <List.Item>
                    <strong>Books:</strong> {impactData.totals.books || 0} books with all content
                  </List.Item>
                  <List.Item>
                    <strong>Characters:</strong> {impactData.totals.characters || 0} characters with profiles
                  </List.Item>
                  <List.Item>
                    <strong>Variations:</strong> {impactData.totals.variations || 0} voice/text variations
                  </List.Item>
                  {impactData.totals.hasProfile && (
                    <List.Item>
                      <strong>Profile:</strong> Literary group profile with all settings
                    </List.Item>
                  )}
                </>
              )}
            </List>
          </Segment>
        ) : (
          <Message>
            <p>Impact analysis will be performed when you open this dialog.</p>
          </Message>
        )}

        {/* Delete Type Selection */}
        <Header as="h4" dividing>
          <Icon name="settings" />
          Delete Options
        </Header>

        <Form className="delete-type-form">
          <Form.Field className="delete-type-field">
            <Form.Radio
              className="delete-type-radio"

              label="Soft Delete (Recommended)"
              value="soft"
              checked={deleteType === 'soft'}
              onChange={handleDeleteTypeChange}
            />

          </Form.Field>
          <div style={{ marginLeft: '50px', marginBottom: '10px', fontSize: '0.9em', color: '#666' }}>
            {getDeleteTypeDescription('soft')}
          </div>
          <Form.Field className="delete-type-field">
            <Form.Radio
              className="delete-type-radio"
              label="Archive"
              value="archive"
              checked={deleteType === 'archive'}
              onChange={handleDeleteTypeChange}
            />

          </Form.Field>
          <div style={{ marginLeft: '50px', marginBottom: '10px', fontSize: '0.9em', color: '#666' }}>
            {getDeleteTypeDescription('archive')}
          </div>
          <Form.Field className="delete-type-field">
            <Form.Radio
              className="delete-type-radio"
              label="Hard Delete (Permanent — removes all data and S3 files)"
              value="hard"
              checked={deleteType === 'hard'}
              onChange={handleDeleteTypeChange}
            />
          </Form.Field>
          <div style={{ marginLeft: '50px', marginBottom: '10px', fontSize: '0.9em', color: '#c00' }}>
            {getDeleteTypeDescription('hard')}
          </div>
        </Form>

        {/* Contract Warning */}
        <Message warning>
          <Message.Header>
            <Icon name="warning" />
            Important Notice
          </Message.Header>
          <p>
            Future contracts may reference characters and content from this literary group.
            {deleteType === 'soft' || deleteType === 'archive'
              ? ' This delete type preserves all data for licensing purposes.'
              : ' Hard delete will permanently remove all data and cannot be undone.'}
          </p>
        </Message>

        {/* Confirmation Input */}
        <Header as="h4" dividing>
          <Icon name="keyboard" />
          Confirmation Required
        </Header>

        <Form.Field required>
          <label>Type "{requiredConfirmation}" to confirm this action:&nbsp;&nbsp;</label>
          <Input
            placeholder={requiredConfirmation}
            value={confirmationText}
            onChange={handleConfirmationTextChange}
            disabled={loading || impactLoading}
            autoComplete="off"
          />
        </Form.Field>

        {deleteType === 'hard' && (
          <Message negative>
            <Message.Header>
              <Icon name="exclamation triangle" />
              Warning: Permanent Deletion
            </Message.Header>
            <p>
              You have selected hard delete. This will permanently remove all data and cannot be undone.
              Only proceed if you are certain no legal or licensing obligations remain.
            </p>
          </Message>
        )}
      </Modal.Content>

      <Modal.Actions>
        <Button
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          color={deleteType === 'hard' ? 'red' : deleteType === 'archive' ? 'orange' : 'yellow'}
          disabled={!canConfirm}
          loading={loading}
          onClick={handleConfirmDelete}
        >
          <Icon name={deleteType === 'hard' ? 'trash' : deleteType === 'archive' ? 'archive' : 'hide'} />
          {getDeleteActionWord(deleteType)} Literary Group
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default DeleteLitGroupModal;
